import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Warning as WarningIcon,
  People as PeopleIcon,
  Share as ShareIcon,
  Edit as EditIcon,
} from "@material-ui/icons";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/styles";

// components
import PageTitle from "../../components/PageTitle";

// styles
import useStyles from "./styles";

// constants
import { SERVER } from "../../constants";

// context
import { useGroupState } from "../../context/GroupContext";

// localization
import { useTranslation, useLeagueNames } from "../../localization";

// helpers
import {
  PredictionsTable,
  matchShareText,
  copyToClipboard,
} from "./components/MatchPredictions";

// logo
import blinkingGreenDot from "../../images/blinking_green_dot.gif";

// Luxon
const { DateTime } = require("luxon");

// Local calendar day (YYYY-MM-DD) a fixture's UTC kickoff falls on, matching the
// UTC->local conversion the Fixtures page uses.
function localDay(fixture) {
  return DateTime.fromISO(fixture.date, { zone: "utc" }).toLocal().toISODate();
}

function kickoffTime(fixture) {
  return DateTime.fromISO(fixture.date, { zone: "utc" })
    .toLocal()
    .toFormat("h:mm a");
}

// The viewer's local time zone abbreviation (e.g. "PDT"), for the "all times are
// local" note.
function localZoneAbbr() {
  return DateTime.local().toFormat("ZZZZ");
}

function isLockedResult(fixture) {
  // A match whose predictions are revealed: in progress or finished.
  return fixture.live || fixture.status === "post";
}

export default function Calendar(props) {
  var classes = useStyles();
  var theme = useTheme();
  // On phones/small tablets the aligned table doesn't fit; fall back to stacked
  // cards. Use the same cutoff (< md) at which the sidebar collapses to its
  // mobile drawer, so the whole app flips to its mobile layout together —
  // otherwise 600–960px devices get mobile chrome but the wide table, which
  // overflows horizontally.
  var isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  var { selectedGroup, leagueNames, isAdmin } = useGroupState();
  var t = useTranslation();
  var leagueOverrides = useLeagueNames();

  const [allInfo, setAllInfo] = useState(null);
  const [predictionsMap, setPredictionsMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [openFixture, setOpenFixture] = useState(null);
  // Fixture whose missing-predictions list is shown in the dialog.
  const [missingFixture, setMissingFixture] = useState(null);
  const [copied, setCopied] = useState(false);
  // Admin edit mode for the open match dialog.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Pull the whole group's fixtures (/allinfo) and the full predictions map
  // (/predictions) in one go. /allinfo only carries per-player predictions for *locked*
  // matches, so the separate /predictions call is what lets us flag upcoming matches that
  // are missing predictions. Resolves with the fresh /allinfo payload so callers
  // (e.g. after an admin save) can refresh the open dialog.
  function loadData() {
    var allInfoUrl = new URL(SERVER + "/allinfo");
    allInfoUrl.search = new URLSearchParams({ group: selectedGroup }).toString();
    var predictionsUrl = new URL(SERVER + "/predictions");
    predictionsUrl.search = new URLSearchParams({
      group: selectedGroup,
    }).toString();

    // no-store so an admin save's refetch reflects the new DB state rather than
    // a browser-cached /allinfo response for the same URL.
    return Promise.all([
      fetch(allInfoUrl, { cache: "no-store" }).then(r => r.json()),
      fetch(predictionsUrl, { cache: "no-store" }).then(r => r.json()),
    ]).then(([allInfoJson, predictionsJson]) => {
      setAllInfo(allInfoJson);
      setPredictionsMap(predictionsJson || {});
      return allInfoJson;
    });
  }

  useEffect(() => {
    setIsLoading(true);
    loadData().then(
      () => setIsLoading(false),
      // Handle errors here (not in a catch) so we don't swallow render-time bugs.
      error => {
        console.debug(error);
        alert(t.calendar.couldNotLoad);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  var fixtures = (allInfo && allInfo.fixtures) || [];
  var today = DateTime.local().toISODate();

  // Day tabs: every day that has fixtures, plus today (always shown), sorted ascending.
  var matchDays = Array.from(new Set(fixtures.map(localDay)));
  var tabDays = Array.from(new Set([...matchDays, today])).sort();

  // Resolve the active day from the route. "today" and out-of-window/unknown slugs all
  // fall back to today.
  var requested = props.day === "today" ? today : props.day;
  var activeDay = tabDays.includes(requested) ? requested : today;

  function dayLabel(day) {
    if (day === today) {
      return t.calendar.today;
    }
    return DateTime.fromISO(day).toFormat("ccc M/d");
  }

  function handleTabChange(event, value) {
    props.history.push("/app/calendar/" + value);
  }

  function leagueLabel(fixture) {
    return (
      leagueOverrides[fixture.league_name] ||
      leagueNames[fixture.league_name] ||
      fixture.official_league_name ||
      fixture.league_name
    );
  }

  // Players in the fixture's league that have no prediction for it. Only meaningful for
  // upcoming/unlocked matches (where predicting is still possible).
  function missingPredictors(fixture) {
    var league = (allInfo.leagues || []).find(
      l => l.name === fixture.league_name,
    );
    var players = (league && league.players) || [];
    var leaguePreds = predictionsMap[fixture.league_name] || {};
    return players.filter(
      p => !(leaguePreds[p.username] && leaguePreds[p.username][fixture.id]),
    );
  }

  var dayMatches = fixtures.filter(f => localDay(f) === activeDay);
  // Show live matches in a dedicated section atop the Today tab; everywhere else they sit
  // inline. (Live matches always fall on today.)
  var liveMatches =
    activeDay === today ? dayMatches.filter(f => f.live) : [];
  var regularMatches = dayMatches.filter(f => !liveMatches.includes(f));
  var lockedMatches = dayMatches.filter(isLockedResult);

  function shareMatch(fixture) {
    copyToClipboard(matchShareText(fixture, t), () => setCopied(true));
  }

  function shareAll() {
    var blocks = [dayLabel(activeDay)];
    lockedMatches.forEach(f => blocks.push(matchShareText(f, t)));
    copyToClipboard(blocks.join("\n\n"), () => setCopied(true));
  }

  // Close the dialog, resetting any in-progress admin edit.
  function closeDialog() {
    setOpenFixture(null);
    setEditing(false);
    setDraft({});
  }

  // Enter admin edit mode, seeding the draft from each player's current
  // prediction so unedited rows submit unchanged values.
  function startEditing() {
    var seed = {};
    (openFixture.predictions || []).forEach(p => {
      seed[p.username] = { home_score: p.home_score, away_score: p.away_score };
    });
    setDraft(seed);
    setEditing(true);
  }

  function onDraftChange(username, field, value) {
    setDraft(prev => ({
      ...prev,
      [username]: { ...prev[username], [field]: value },
    }));
  }

  // Normalize a draft score for the wire: integers like the Fixtures page sends
  // (the server stores ints), null to clear a prediction. Inputs arrive as
  // strings from the number field.
  function toScore(value) {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    var n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
  }

  // Persist admin edits: POST one prediction per player whose draft differs from
  // their stored prediction, then refetch so the dialog shows recomputed points.
  function saveEdits() {
    var changed = (openFixture.predictions || []).filter(p => {
      var d = draft[p.username] || {};
      return (
        String(d.home_score) !== String(p.home_score) ||
        String(d.away_score) !== String(p.away_score)
      );
    });
    if (changed.length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    Promise.all(
      changed.map(p =>
        fetch(SERVER + "/predictions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            group: selectedGroup,
            league: openFixture.league_name,
            name: p.username,
            predictions: {
              [openFixture.id]: {
                home_score: toScore(draft[p.username].home_score),
                away_score: toScore(draft[p.username].away_score),
              },
            },
          }),
        }).then(r => r.json()),
      ),
    ).then(
      () =>
        loadData().then(allInfoJson => {
          var fresh = ((allInfoJson && allInfoJson.fixtures) || []).find(
            f => f.id === openFixture.id,
          );
          if (fresh) {
            setOpenFixture(fresh);
          }
          setEditing(false);
          setSaving(false);
          setSaved(true);
        }),
      error => {
        console.debug(error);
        alert(t.calendar.couldNotSave);
        setSaving(false);
      },
    );
  }

  function renderScore(fixture) {
    if (fixture.live) {
      return (
        <span className={classes.liveData}>
          {fixture.home_score} - {fixture.away_score}
          <img
            src={blinkingGreenDot}
            alt="live"
            className={classes.blinkingGreenDot}
          />
          {fixture.display_clock ? (
            <span>&nbsp;{fixture.display_clock}</span>
          ) : null}
        </span>
      );
    }
    if (fixture.status === "post") {
      return (
        <span>
          {fixture.home_score} - {fixture.away_score}
          {fixture.has_post_reg_goals
            ? " (" +
              t.share.regLabel +
              ": " +
              fixture.home_score_reg +
              " - " +
              fixture.away_score_reg +
              ")"
            : ""}
        </span>
      );
    }
    return (
      <Typography variant="body2" color="textSecondary">
        {fixture.status_detail || t.calendar.scheduled}
      </Typography>
    );
  }

  // The missing-predictions warning, shared by the table and card layouts.
  function warningFor(fixture) {
    if (!(fixture.status === "pre" && !fixture.locked)) {
      return null;
    }
    var missing = missingPredictors(fixture);
    if (missing.length === 0) {
      return null;
    }
    var names = missing.map(p => p.name);
    return (
      <Tooltip
        title={
          <span style={{ whiteSpace: "pre-line" }}>
            {t.calendar.missingPredictionsTitle}
            {"\n" + names.join("\n")}
          </span>
        }
        leaveDelay={300}
        leaveTouchDelay={3000}
      >
        <IconButton
          size="small"
          className={classes.warningButton}
          onClick={() => setMissingFixture(fixture)}
        >
          <WarningIcon className={classes.warningIcon} />
        </IconButton>
      </Tooltip>
    );
  }

  // The "view predictions" button for locked matches, shared by both layouts.
  function viewButton(fixture) {
    if (!isLockedResult(fixture)) {
      return null;
    }
    return (
      <Tooltip title={t.calendar.viewPredictions}>
        <IconButton size="small" onClick={() => setOpenFixture(fixture)}>
          <PeopleIcon />
        </IconButton>
      </Tooltip>
    );
  }

  function teamsCell(fixture) {
    return (
      <span className={classes.teams}>
        <img src={fixture.home_flag_url} alt="" className={classes.teamFlag} />
        {fixture.home_team} vs {fixture.away_team}
        <img src={fixture.away_flag_url} alt="" className={classes.teamFlag} />
      </span>
    );
  }

  // Desktop/tablet: aligned table row (+ a full-width expansion row when "Show all").
  function renderMatchRow(fixture) {
    var locked = isLockedResult(fixture);
    var expanded = locked && showAll;
    return (
      <React.Fragment key={fixture.id}>
        <TableRow
          className={`${fixture.live ? classes.liveRow : ""} ${
            expanded ? classes.expandedRow : ""
          }`}
        >
          <TableCell className={classes.kickoff}>
            {kickoffTime(fixture)}
          </TableCell>
          <TableCell>{teamsCell(fixture)}</TableCell>
          <TableCell className={classes.league}>
            {leagueLabel(fixture)}
          </TableCell>
          <TableCell>
            <span className={classes.score}>{renderScore(fixture)}</span>
          </TableCell>
          <TableCell className={classes.actionsCell}>
            {warningFor(fixture)}
            {viewButton(fixture)}
          </TableCell>
        </TableRow>
        {expanded ? (
          <TableRow>
            <TableCell colSpan={5} className={classes.expansionCell}>
              <PredictionsTable
                fixture={fixture}
                className={classes.predictionsTable}
              />
            </TableCell>
          </TableRow>
        ) : null}
      </React.Fragment>
    );
  }

  // Phones: a compact stacked card (time on the left, teams on top, then league /
  // score / actions; predictions expand beneath).
  function renderMatchCard(fixture) {
    var expanded = isLockedResult(fixture) && showAll;
    return (
      <div
        key={fixture.id}
        className={`${classes.mCard} ${fixture.live ? classes.mLiveCard : ""}`}
      >
        <span className={classes.mTime}>{kickoffTime(fixture)}</span>
        <div className={classes.mBody}>
          {teamsCell(fixture)}
          <div className={classes.mMeta}>
            <span className={classes.mLeague}>{leagueLabel(fixture)}</span>
            <span className={classes.score}>{renderScore(fixture)}</span>
            <span className={classes.mActions}>
              {warningFor(fixture)}
              {viewButton(fixture)}
            </span>
          </div>
          {expanded ? (
            <div className={classes.mExpansion}>
              <PredictionsTable
                fixture={fixture}
                className={classes.predictionsTable}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function renderMatchList(matches) {
    if (isMobile) {
      return <div>{matches.map(renderMatchCard)}</div>;
    }
    return (
      <Table size="small" className={classes.matchTable}>
        <TableBody>{matches.map(renderMatchRow)}</TableBody>
      </Table>
    );
  }

  return (
    <>
      <PageTitle title={t.calendar.title} />

      <Typography variant="body2" className={classes.tzNote}>
        {t.common.localTimeNote(localZoneAbbr())}
      </Typography>

      <div className={classes.toolbar}>
        {lockedMatches.length > 0 ? (
          <>
            <FormControlLabel
              className={classes.toggle}
              control={
                <Switch
                  checked={showAll}
                  color="primary"
                  onChange={e => setShowAll(e.target.checked)}
                />
              }
              label={t.calendar.showAllPredictions}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<ShareIcon />}
              onClick={shareAll}
            >
              {t.common.share}
            </Button>
          </>
        ) : null}
      </div>

      <Tabs
        value={activeDay}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.tabs}
      >
        {tabDays.map(day => (
          <Tab key={day} label={dayLabel(day)} value={day} />
        ))}
      </Tabs>

      {isLoading ? (
        <LinearProgress />
      ) : (
        <Grid container>
          <Grid item xs={12}>
            {liveMatches.length > 0 ? (
              <>
                <Typography variant="h5" className={classes.liveHeader}>
                  <img
                    src={blinkingGreenDot}
                    alt="live"
                    className={classes.blinkingGreenDot}
                  />
                  &nbsp;{t.calendar.liveNow}
                </Typography>
                {renderMatchList(liveMatches)}
                <Typography variant="h5" className={classes.sectionHeader}>
                  {t.calendar.matches}
                </Typography>
              </>
            ) : null}

            {regularMatches.length > 0 ? (
              renderMatchList(regularMatches)
            ) : liveMatches.length === 0 ? (
              <Typography variant="body1" className={classes.emptyDay}>
                {t.calendar.noMatches}
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      )}

      {/* disableEnforceFocus so the clipboard fallback's temporary <textarea> can hold
          focus long enough for execCommand("copy") to land (the app is served over http
          on the LAN, where navigator.clipboard is unavailable). */}
      <Dialog open={!!openFixture} onClose={closeDialog} disableEnforceFocus>
        {openFixture ? (
          <>
            <DialogTitle className={classes.dialogTitle}>
              <span>
                {openFixture.home_team} {openFixture.home_score} -{" "}
                {openFixture.away_score} {openFixture.away_team}
              </span>
              {isAdmin && !editing ? (
                <Tooltip title={t.calendar.editPredictions}>
                  <IconButton size="small" onClick={startEditing}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </DialogTitle>
            <DialogContent className={classes.dialogPredictions}>
              <PredictionsTable
                fixture={openFixture}
                className={classes.predictionsTable}
                editing={editing}
                draft={draft}
                onDraftChange={onDraftChange}
              />
            </DialogContent>
            <DialogActions>
              {editing ? (
                <>
                  <Button
                    onClick={() => {
                      setEditing(false);
                      setDraft({});
                    }}
                    disabled={saving}
                  >
                    {t.common.cancel}
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={saveEdits}
                    disabled={saving}
                  >
                    {saving ? t.common.saving : t.common.save}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color="primary"
                    startIcon={<ShareIcon />}
                    onClick={() => shareMatch(openFixture)}
                  >
                    {t.common.share}
                  </Button>
                  <Button onClick={closeDialog}>{t.common.close}</Button>
                </>
              )}
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* disableEnforceFocus so the clipboard fallback's temporary <textarea> can hold
          focus long enough for execCommand("copy") to land (the app is served over http
          on the LAN, where navigator.clipboard is unavailable). */}
      <Dialog
        open={!!missingFixture}
        onClose={() => setMissingFixture(null)}
        disableEnforceFocus
        fullWidth
        maxWidth="xs"
      >
        {missingFixture ? (
          <>
            <DialogTitle>{t.calendar.missingPredictionsTitle}</DialogTitle>
            <DialogContent>
              {missingPredictors(missingFixture).map(p => (
                <Typography key={p.username} variant="body1">
                  {p.name}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                color="primary"
                onClick={() =>
                  copyToClipboard(
                    missingPredictors(missingFixture)
                      .map(p => p.name)
                      .join("\n"),
                    () => setCopied(true),
                  )
                }
              >
                {t.common.copy}
              </Button>
              <Button onClick={() => setMissingFixture(null)}>
                {t.common.close}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        message={t.calendar.copied}
        onClose={() => setCopied(false)}
      />

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        message={t.calendar.saved}
        onClose={() => setSaved(false)}
      />
    </>
  );
}
